/**
 * Calendar-day formatting for values the API sends as "YYYY-MM-DD" (optionally followed by a time part,
 * e.g. mission_date). The day is taken from the string parts, never through `new Date("YYYY-MM-DD")`,
 * which parses as UTC midnight and shows the previous day west of UTC.
 * The displayed format is DD/MM/YYYY in both app languages.
 */

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/;
const DMY_DATE = /^(\d{1,2})([./])(\d{1,2})\2(\d{4})$/;

const pad2 = (n) => String(n).padStart(2, "0");

const isCalendarDay = (year, month, day) => {
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

/** "2026-10-15" or "2026-10-15T00:00:00Z" → "15/10/2026"; empty → "-"; anything else is returned as is. */
export const formatIsoDateDmy = (value) => {
  if (!value) return "-";
  const match = ISO_DATE.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

/** "15/10/2026" (also "5/3/2026" or "15.10.2026") → "2026-10-15"; an incomplete or non-existent day → null. */
export const parseDmyToIsoDate = (text) => {
  const match = DMY_DATE.exec((text || "").trim());
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[3]);
  const year = Number(match[4]);
  if (!isCalendarDay(year, month, day)) return null;
  return `${year}-${pad2(month)}-${pad2(day)}`;
};

/** "2026-10-15" → local-midnight Date for a calendar widget; anything else → undefined. */
export const isoDateToLocalDate = (value) => {
  const match = ISO_DATE.exec(value || "");
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return isCalendarDay(year, month, day) ? new Date(year, month - 1, day) : undefined;
};

/** Local Date picked in a calendar widget → "2026-10-15" (local calendar day, no UTC conversion). */
export const localDateToIsoDate = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
